import { useEffect, useRef } from 'react'
import { Utils as utils } from '@client/utility'
import { GetUserUUID } from '@components'
import Cookies from 'js-cookie'
import FingerprintJS from '@fingerprintjs/fingerprintjs'

const powState = { running: false, done: false }

interface ProofTokens {
  authentication_token?: string
  poftok?: string
  user_token?: string
  device_token?: string
}

interface PoWOptions {
  public_ip?: string
}

export function useProofOfWork(
  onSuccess: (tokens: ProofTokens) => void,
  { public_ip }: PoWOptions
) {
  const verifiedRef = useRef(false)

  useEffect(() => {
    if (!public_ip || public_ip === 'unknown') return
    if (verifiedRef.current || powState.running || powState.done) return

    const startPoW = async () => {
      powState.running = true

      try {
        const { challenge, difficulty } = await utils.post<{
          challenge: string
          difficulty: number
        }>('/api/GetProofChallenge', {})

        const maxAttempts = 500_000
        const worker = new Worker('/worker/powWorker.js')
        worker.postMessage({ challenge, difficulty, maxAttempts })

        worker.onmessage = async (e: any) => {
          const data = e.data

          if (data.success && !powState.done) {
            try {
              const user_uuid = await GetUserUUID()
              const fp = await FingerprintJS.load()
              const fingerprint = (await fp.get()).visitorId

              const verifyRes = await utils.post<{
                success: boolean
                token?: string
                poftok?: string
                user_token?: string
                device_token?: string
              }>('/api/VerifyProof', {
                challenge,
                solution: data.nonce,
                user_uuid,
                fingerprint,
                public_ip,
                ua: navigator.userAgent,
                lang: navigator.language,
              })

              if (verifyRes.success) {
                const tokens: ProofTokens = {
                  authentication_token: verifyRes.token,
                  poftok: verifyRes.poftok,
                  user_token: verifyRes.user_token,
                  device_token: verifyRes.device_token,
                }

                const cookieOptions = {
                  secure: true,
                  sameSite: 'Strict' as const,
                  httpOnly: false,
                  expires: 7,
                }

                if (tokens.authentication_token)
                  Cookies.set('authentication_token', tokens.authentication_token, cookieOptions)
                if (tokens.poftok)
                  Cookies.set('poftok', tokens.poftok, cookieOptions)
                if (tokens.user_token)
                  Cookies.set('user_token', tokens.user_token, cookieOptions)
                if (tokens.device_token)
                  Cookies.set('device-token', tokens.device_token, cookieOptions)

                verifiedRef.current = true
                powState.done = true
                powState.running = false
                onSuccess(tokens)
              }
            } catch (err) {
              console.error('[PoW] Verification error:', err)
            } finally {
              worker.terminate()
            }
          } else if (!data.progress) {
            console.error('[PoW] Failed to solve challenge.')
            powState.running = false
            worker.terminate()
          }
        }
      } catch (err) {
        console.error('[PoW] Failed to fetch challenge:', err)
        powState.running = false
      }
    }

    startPoW()
  }, [onSuccess, public_ip])
}